/**
 * JOVARKUS LUXURY THEME — JS
 * Pure Native Vanilla JavaScript
 */

(function () {
  'use strict';

  // --------------------------------------------------------------------------
  // Currency Formatter Helper
  // --------------------------------------------------------------------------
  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    const formatString = window.theme?.moneyFormat || '${{amount}}';
    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    switch (formatString.match(placeholderRegex)?.[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // --------------------------------------------------------------------------
  // Sticky Header Scroll Controller
  // --------------------------------------------------------------------------
  function initStickyHeader() {
    const header = document.querySelector('.js-site-header');
    if (!header || header.dataset.sticky !== 'true') return;

    let lastScrollY = window.scrollY;

    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 40) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
      lastScrollY = currentScrollY;
    }, { passive: true });
  }

  // --------------------------------------------------------------------------
  // Mobile Navigation Drawer
  // --------------------------------------------------------------------------
  function initMobileMenu() {
    const drawer = document.getElementById('MobileNavDrawer');
    const openBtn = document.querySelector('.js-mobile-menu-toggle');
    const closeBtns = document.querySelectorAll('.js-mobile-menu-close');
    if (!drawer || !openBtn) return;

    function openMenu() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function closeMenu() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    openBtn.addEventListener('click', openMenu);
    closeBtns.forEach((btn) => btn.addEventListener('click', closeMenu));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeMenu();
      }
    });
  }

  // --------------------------------------------------------------------------
  // Cart Drawer Manager (AJAX)
  // --------------------------------------------------------------------------
  const CartDrawer = {
    drawer: null,
    itemsWrapper: null,
    emptyNotice: null,
    footer: null,
    subtotal: null,
    countBadges: null,
    shippingBar: null,
    shippingMsg: null,
    shippingFill: null,

    init() {
      this.drawer = document.getElementById('CartDrawer');
      if (!this.drawer) return;

      this.itemsWrapper = this.drawer.querySelector('.js-cart-items-wrapper');
      this.emptyNotice = this.drawer.querySelector('.js-cart-drawer-empty');
      this.footer = this.drawer.querySelector('.js-cart-drawer-footer');
      this.subtotal = this.drawer.querySelector('.js-cart-subtotal');
      this.countBadges = document.querySelectorAll('.js-cart-bubble, .js-cart-count');
      this.shippingBar = this.drawer.querySelector('.cart-drawer__shipping-bar');
      this.shippingMsg = this.drawer.querySelector('.js-shipping-bar-msg');
      this.shippingFill = this.drawer.querySelector('.js-shipping-bar-fill');

      this.bindEvents();
    },

    bindEvents() {
      // Open / Close triggers
      document.querySelectorAll('.js-cart-open').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      this.drawer.querySelectorAll('.js-cart-drawer-close').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Quantity adjustments in Cart Drawer
      this.drawer.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('.js-cart-qty-btn');
        if (qtyBtn) {
          const key = qtyBtn.dataset.key;
          const qty = parseInt(qtyBtn.dataset.qty, 10);
          this.updateItem(key, qty);
          return;
        }

        const removeBtn = e.target.closest('.js-cart-remove');
        if (removeBtn) {
          const key = removeBtn.dataset.key;
          this.updateItem(key, 0);
        }
      });

      // Cart notes auto-save
      const notesInput = this.drawer.querySelector('.js-cart-notes-input');
      if (notesInput) {
        notesInput.addEventListener('change', () => {
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: notesInput.value }),
          });
        });
      }
    },

    isOpen() {
      return this.drawer && this.drawer.classList.contains('is-open');
    },

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.refresh();
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    async refresh() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.render(cart);
      } catch (err) {
        console.error('Error fetching cart:', err);
      }
    },

    async updateItem(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
        });
        const cart = await res.json();
        this.render(cart);
      } catch (err) {
        console.error('Error updating cart line item:', err);
      }
    },

    render(cart) {
      // 1. Update header and drawer counts
      this.countBadges.forEach((el) => {
        if (el.classList.contains('js-cart-bubble')) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        } else if (el.classList.contains('js-cart-count')) {
          el.textContent = `(${cart.item_count})`;
        }
      });

      // 2. Empty state vs items
      if (cart.item_count === 0) {
        if (this.emptyNotice) this.emptyNotice.classList.remove('hidden');
        if (this.footer) this.footer.classList.add('hidden');
        if (this.itemsWrapper) this.itemsWrapper.innerHTML = '';
        return;
      }

      if (this.emptyNotice) this.emptyNotice.classList.add('hidden');
      if (this.footer) this.footer.classList.remove('hidden');

      // 3. Subtotal
      if (this.subtotal) {
        this.subtotal.textContent = formatMoney(cart.total_price);
      }

      // 4. Free shipping threshold bar
      if (this.shippingBar && this.shippingMsg && this.shippingFill) {
        const goal = parseInt(this.shippingBar.dataset.goal, 10) || 15000;
        if (cart.total_price >= goal) {
          this.shippingMsg.textContent = 'You are eligible for complimentary shipping';
          this.shippingFill.style.width = '100%';
        } else {
          const diff = goal - cart.total_price;
          this.shippingMsg.textContent = `Add ${formatMoney(diff)} more for complimentary shipping`;
          const pct = Math.min(100, Math.round((cart.total_price / goal) * 100));
          this.shippingFill.style.width = `${pct}%`;
        }
      }

      // 5. Items list markup
      if (this.itemsWrapper) {
        let html = '<ul class="cart-drawer__item-list">';
        cart.items.forEach((item) => {
          const imageSrc = item.image ? item.image : '';
          const hasVariant = item.variant_title && item.variant_title !== 'Default Title';
          const originalPrice = item.original_price !== item.final_price
            ? `<s class="cart-item__original-price">${formatMoney(item.original_price)}</s> `
            : '';

          html += `
            <li class="cart-item" data-line-item-key="${item.key}">
              <div class="cart-item__media">
                ${imageSrc ? `<img src="${imageSrc}" alt="${item.product_title}" class="cart-item__image" width="90" height="120" loading="lazy">` : ''}
              </div>
              <div class="cart-item__details">
                <div class="cart-item__header">
                  <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
                  <button type="button" class="cart-item__remove js-cart-remove" data-key="${item.key}" aria-label="Remove item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                </div>
                ${hasVariant ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
                <div class="cart-item__price">
                  ${originalPrice}<span class="cart-item__final-price">${formatMoney(item.final_price)}</span>
                </div>
                <div class="cart-item__quantity">
                  <div class="quantity-input quantity-input--sm">
                    <button type="button" class="quantity-input__btn js-cart-qty-btn" data-action="minus" data-key="${item.key}" data-qty="${item.quantity - 1}">&#8722;</button>
                    <span class="quantity-input__display">${item.quantity}</span>
                    <button type="button" class="quantity-input__btn js-cart-qty-btn" data-action="plus" data-key="${item.key}" data-qty="${item.quantity + 1}">&#43;</button>
                  </div>
                </div>
              </div>
            </li>
          `;
        });
        html += '</ul>';
        this.itemsWrapper.innerHTML = html;
      }
    },
  };

  // --------------------------------------------------------------------------
  // AJAX Add to Bag (Forms and Quick Add)
  // --------------------------------------------------------------------------
  function initAddToCart() {
    // Form submissions (PDP / Quick forms)
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('.js-product-form');
      if (!form) return;

      e.preventDefault();
      const submitBtn = form.querySelector('.js-add-to-cart-btn');
      const submitText = submitBtn?.querySelector('.btn__text');
      const submitLoading = submitBtn?.querySelector('.btn__loading');

      if (submitBtn) submitBtn.disabled = true;
      if (submitText && submitLoading) {
        submitText.classList.add('hidden');
        submitLoading.classList.remove('hidden');
      }

      const formData = new FormData(form);

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) throw new Error('Add to cart failed');

        CartDrawer.open();
      } catch (err) {
        console.error(err);
        alert('Could not add item to bag. Please try again.');
      } finally {
        if (submitBtn) submitBtn.disabled = false;
        if (submitText && submitLoading) {
          submitText.classList.remove('hidden');
          submitLoading.classList.add('hidden');
        }
      }
    });

    // Quick Add button on product cards
    document.addEventListener('click', async (e) => {
      const quickAddBtn = e.target.closest('.js-quick-add');
      if (!quickAddBtn) return;

      e.preventDefault();
      const variantId = quickAddBtn.dataset.variantId;
      if (!variantId) return;

      const textSpan = quickAddBtn.querySelector('.quick-add__text');
      const loadSpan = quickAddBtn.querySelector('.quick-add__loading');

      quickAddBtn.disabled = true;
      if (textSpan && loadSpan) {
        textSpan.classList.add('hidden');
        loadSpan.classList.remove('hidden');
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });

        if (!res.ok) throw new Error('Quick add failed');

        CartDrawer.open();
      } catch (err) {
        console.error(err);
      } finally {
        quickAddBtn.disabled = false;
        if (textSpan && loadSpan) {
          textSpan.classList.remove('hidden');
          loadSpan.classList.add('hidden');
        }
      }
    });
  }

  // --------------------------------------------------------------------------
  // Product Detail Page Variant & Image Logic
  // --------------------------------------------------------------------------
  function initProductPage() {
    const section = document.querySelector('.main-product-section');
    if (!section) return;

    const variantsJsonEl = section.querySelector('.js-product-variants-json');
    let variants = [];
    if (variantsJsonEl) {
      try {
        variants = JSON.parse(variantsJsonEl.textContent);
      } catch (e) {
        console.error('Failed to parse variant JSON', e);
      }
    }

    const form = section.querySelector('.js-product-form');
    const hiddenIdInput = form?.querySelector('.js-variant-id');
    const mainImg = section.querySelector('.js-main-image');
    const priceBox = section.querySelector('.js-product-price');
    const skuEl = section.querySelector('.js-product-sku');
    const availEl = section.querySelector('.js-product-availability');
    const submitBtn = section.querySelector('.js-add-to-cart-btn');

    // Thumbnail switching
    const thumbnails = section.querySelectorAll('.js-thumbnail-btn');
    thumbnails.forEach((btn) => {
      btn.addEventListener('click', () => {
        thumbnails.forEach((t) => t.classList.remove('is-active'));
        btn.classList.add('is-active');
        const fullSrc = btn.dataset.fullImg;
        if (mainImg && fullSrc) {
          mainImg.src = fullSrc;
        }
      });
    });

    // Quantity selectors (+ / -)
    section.querySelectorAll('.js-qty-plus, .js-qty-minus').forEach((btn) => {
      btn.addEventListener('click', () => {
        const field = section.querySelector('.js-qty-field');
        if (!field) return;
        let val = parseInt(field.value, 10) || 1;
        if (btn.classList.contains('js-qty-plus')) {
          val += 1;
        } else if (val > 1) {
          val -= 1;
        }
        field.value = val;
      });
    });

    // Variant change observer
    function updateVariant() {
      if (!variants.length) return;

      const selectedOptions = [];
      const optionContainers = section.querySelectorAll('.product-form__option');

      optionContainers.forEach((optContainer) => {
        const radio = optContainer.querySelector('.js-option-radio:checked');
        const select = optContainer.querySelector('.js-option-select');
        const label = optContainer.querySelector('.js-option-value-label');

        let val = '';
        if (radio) val = radio.value;
        else if (select) val = select.value;

        if (label && val) label.textContent = val;
        selectedOptions.push(val);
      });

      // Find matching variant
      const currentVariant = variants.find((v) => {
        return v.options.every((optVal, idx) => optVal === selectedOptions[idx]);
      });

      if (!currentVariant) {
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.querySelector('.btn__text').textContent = window.theme?.strings?.unavailable || 'Unavailable';
        }
        return;
      }

      // Update hidden id
      if (hiddenIdInput) hiddenIdInput.value = currentVariant.id;

      // Update price
      if (priceBox) {
        const isSale = currentVariant.compare_at_price > currentVariant.price;
        let priceHtml = `
          <div class="price-container ${isSale ? 'price--on-sale' : ''}">
            ${isSale ? `<span class="price-item price-item--sale">${formatMoney(currentVariant.price)}</span>` : `<span class="price-item price-item--regular">${formatMoney(currentVariant.price)}</span>`}
            ${isSale ? `<s class="price-item price-item--regular price-item--compare">${formatMoney(currentVariant.compare_at_price)}</s>` : ''}
          </div>
        `;
        priceBox.innerHTML = priceHtml;
      }

      // Update SKU & availability
      if (skuEl) {
        skuEl.textContent = currentVariant.sku ? `SKU: ${currentVariant.sku}` : '';
      }
      if (availEl) {
        if (currentVariant.available) {
          availEl.innerHTML = '<span class="in-stock-dot"></span> In Stock — Ready to dispatch';
        } else {
          availEl.innerHTML = '<span class="out-of-stock-dot"></span> Currently unavailable';
        }
      }

      // Update Add to Cart Button
      if (submitBtn) {
        submitBtn.disabled = !currentVariant.available;
        const textSpan = submitBtn.querySelector('.btn__text');
        if (textSpan) {
          textSpan.textContent = currentVariant.available
            ? window.theme?.strings?.addToCart || 'Add to Bag'
            : window.theme?.strings?.soldOut || 'Sold Out';
        }
      }

      // Switch image if variant has media
      if (currentVariant.featured_image && mainImg) {
        mainImg.src = currentVariant.featured_image.src;
      }

      // Update browser URL history state
      const url = new URL(window.location.href);
      url.searchParams.set('variant', currentVariant.id);
      window.history.replaceState({}, '', url);
    }

    section.addEventListener('change', (e) => {
      if (e.target.matches('.js-option-radio, .js-option-select')) {
        updateVariant();
      }
    });

    // Sticky ATC on mobile
    const stickyAtc = section.querySelector('.js-sticky-atc');
    const triggerAtcBtn = section.querySelector('.js-trigger-atc');
    if (stickyAtc && submitBtn) {
      triggerAtcBtn?.addEventListener('click', () => {
        submitBtn.click();
      });

      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) {
          stickyAtc.classList.add('is-visible');
        } else {
          stickyAtc.classList.remove('is-visible');
        }
      }, { threshold: 0 });

      observer.observe(submitBtn);
    }
  }

  // --------------------------------------------------------------------------
  // Predictive Search Modal Controller
  // --------------------------------------------------------------------------
  function initSearchModal() {
    const modal = document.getElementById('SearchModal');
    const openBtns = document.querySelectorAll('.js-search-open');
    const closeBtns = document.querySelectorAll('.js-search-close');
    const input = document.getElementById('SearchModalInput');
    const suggestions = modal?.querySelector('.js-search-suggestions');
    const results = modal?.querySelector('.js-search-results');
    const resultsGrid = modal?.querySelector('.js-results-grid');
    const viewAllBtn = modal?.querySelector('.js-view-all-results');

    if (!modal) return;

    function openModal() {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input?.focus(), 100);
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    openBtns.forEach((btn) => btn.addEventListener('click', openModal));
    closeBtns.forEach((btn) => btn.addEventListener('click', closeModal));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) {
        closeModal();
      }
    });

    // Debounced predictive search query
    let debounceTimer = null;
    input?.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const query = input.value.trim();

      if (!query || query.length < 2) {
        suggestions?.classList.remove('hidden');
        results?.classList.add('hidden');
        return;
      }

      debounceTimer = setTimeout(async () => {
        try {
          const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4`);
          const data = await res.json();
          const products = data.resources?.results?.products || [];

          suggestions?.classList.add('hidden');
          results?.classList.remove('hidden');

          if (viewAllBtn) {
            viewAllBtn.href = `/search?q=${encodeURIComponent(query)}&type=product`;
          }

          if (resultsGrid) {
            if (products.length === 0) {
              resultsGrid.innerHTML = '<p class="search-empty-msg">No products matching your search.</p>';
            } else {
              let html = '';
              products.forEach((p) => {
                html += `
                  <div class="product-card">
                    <div class="product-card__media-wrapper product-card__media-wrapper--portrait">
                      <a href="${p.url}" class="product-card__link">
                        ${p.image ? `<img src="${p.image}" alt="${p.title}" class="product-card__image" loading="lazy">` : ''}
                      </a>
                    </div>
                    <div class="product-card__info">
                      <h3 class="product-card__title">
                        <a href="${p.url}" class="product-card__title-link">${p.title}</a>
                      </h3>
                      <div class="price-container">
                        <span class="price-item price-item--regular">${formatMoney(p.price)}</span>
                      </div>
                    </div>
                  </div>
                `;
              });
              resultsGrid.innerHTML = html;
            }
          }
        } catch (err) {
          console.error('Search error:', err);
        }
      }, 300);
    });
  }

  // --------------------------------------------------------------------------
  // Collection Sorting & Filters
  // --------------------------------------------------------------------------
  function initCollectionSort() {
    const sortSelect = document.querySelector('.js-sort-by');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });

    const filterForm = document.querySelector('.js-filters-form');
    if (filterForm) {
      filterForm.addEventListener('change', () => {
        filterForm.submit();
      });
    }
  }

  // --------------------------------------------------------------------------
  // Cart Page Quantities
  // --------------------------------------------------------------------------
  function initCartPage() {
    document.querySelectorAll('.js-cart-page-qty').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = btn.dataset.line;
        const qty = parseInt(btn.dataset.qty, 10);
        window.location.href = `/cart/change?line=${line}&quantity=${Math.max(0, qty)}`;
      });
    });
  }

  // --------------------------------------------------------------------------
  // Wishlist Button Micro-interaction
  // --------------------------------------------------------------------------
  function initWishlistToggle() {
    document.addEventListener('click', (e) => {
      const wishlistBtn = e.target.closest('.js-wishlist-toggle');
      if (!wishlistBtn) return;
      e.preventDefault();
      e.stopPropagation();

      wishlistBtn.classList.toggle('is-active');
      const icon = wishlistBtn.querySelector('svg');
      if (wishlistBtn.classList.contains('is-active')) {
        wishlistBtn.style.color = '#B89D75';
        if (icon) {
          icon.style.fill = '#B89D75';
          icon.style.stroke = '#B89D75';
        }
      } else {
        wishlistBtn.style.color = '';
        if (icon) {
          icon.style.fill = 'none';
          icon.style.stroke = 'currentColor';
        }
      }
    });
  }

  // --------------------------------------------------------------------------
  // DOM Ready Initializer
  // --------------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', () => {
    initStickyHeader();
    initMobileMenu();
    CartDrawer.init();
    initAddToCart();
    initProductPage();
    initSearchModal();
    initCollectionSort();
    initCartPage();
    initWishlistToggle();
  });
})();
